<script setup>
import { useDetailsReport } from '#/detailsReport/useDetailsReport'
import { computed, inject } from 'vue'
import { useFilters } from '#/general/useFilters'
import PotentialLineItem from '#/detailsReport/PotentialLineItem'
import AnalysisTooltipContent from '#/detailsReport/AnalysisTooltipContent'
import TooltipContainer from '#/general/TooltipContainer'
import { useGA } from '#/useGA'

const { potential } = useDetailsReport()
const { prettifyNumber } = useFilters()
const { global, screen } = inject('Shared')
const { event } = useGA()

const biggestBuildingZone = computed(() => {
  // eslint-disable-next-line vue/no-side-effects-in-computed-properties
  return potential.value.buildingZones.sort((a, b) => a.squareMeters < b.squareMeters ? 1 : -1)[0]
})
</script>

<template lang="pug">
.potential-details--container
  .infoBox.firstBox(v-if="potential")
    .box(style="width: 100%;")
      .boxLabel Eckdaten und berechnungsmethode
      potential-line-item(label="Gebäudekategorie:" tooltip="Einteilung der Gebäude nach EUROSTAT-Klassifikation+." :val="potential.buildings[0].buildingCategory")
      potential-line-item(label="Parzellen-Nummer:" tooltip="Die eindeutige Parzellen-Nummer der Gemeinde, in welcher sich die abgefragte Liegenschaft befindet." :val="`${potential.parcel.parcelNr ? potential.parcel.parcelNr : 'Unknown'}`")
      potential-line-item(label="Grundstücksfläche:" :val="(prettifyNumber(Math.round(potential.parcel.area), 0))" unit="m" small-text="2")
      potential-line-item(label="Gebäudegrundfläche:" tooltip="Die Gebäudegrundfläche ist gemäss SIA 416 jene Fläche des Grundstücks, welche von Gebäuden oder Gebäudeteilen durchdrungen wird." :val="prettifyNumber(Math.round(potential.parcel.builtUpArea), 0)" unit="m" small-text="2")
      potential-line-item(label="Genauigkeit der Analyse:" :tooltip-offset="[210, 0]" )
        template(#tooltipContent)
          tooltip-container(
            :title="potential.analysis.isBuildingZoneBased ? 'Hohe Genauigkeit' : 'Mittlere Genauigkeit (gelb)'"
            :body="potential.analysis.isBuildingZoneBased ? 'Diese Potenzialberechnung basiert auf dem lokalen kommunalen Baureglement und verfügt somit über eine hohe Genauigkeit.' : 'Diese Potenzialberechnung basiert auf der Bebauungsdichte der direkten Umgebung bzw. Nachbarschaft.'"
            )
            template(#chip)
              div(:class="potential.analysis.isBuildingZoneBased ? 'filledChip' : 'notFilledChip'")
        .itemValue(v-if="potential.analysis.isBuildingZoneBased").row
          .svgCircleAndTitleFirstPartContainer
            .svgCircle.shadow-1
              .svgCircle--green
            .itemValueTitle Hohe
          .itemValueTitle Genauigkeit
        span.itemValue.row.items-center.justify-end(v-else)
          span.svgCircle.shadow-1
            .svgCircle--yellow
          span.itemValueTitle.q-pr-xs Mittlere
          span.itemValueTitle Genauigkeit
  .infoBox.secondBox(v-if="potential")
    .box(style="width: 100%;")
      .boxLabel baurecht
      potential-line-item(label="Kommunale Bauzone:" tooltip="Die auf Gemeindeebene definierte Bauzone, in welcher sich die abgefragte Liegenschaft befindet." )
        .potential-line-item--bauZone-container
          template(v-for="(potentialBuildingZone, index) in potential.buildingZones" :key="index")
            .itemValue(v-if="potentialBuildingZone.buildingZone")
              span.relative-position  {{ `${potentialBuildingZone.buildingZone.shortName} (${potentialBuildingZone.squareMeters}m` }}
                .smallText 2
              span.q-pl-sm )
      potential-line-item(label="Max. zulässige Vollgeschosse:" :val="biggestBuildingZone.buildingZone && biggestBuildingZone.buildingZone.fullFloors ? biggestBuildingZone.buildingZone.fullFloors  + ' Vollgeschosse' : ''" width="350px")
        template(#tooltipContent)
          analysis-tooltip-content(
            label="Vollgeschosse"
            tooltip-text="Anzahl der gemäss Baugesetz  möglichen Vollgeschosse (grün). Achtung: Keller- sowie Dachgeschosse gelten nicht als Vollgeschosse und werden deshalb nicht eingerechnet."
          )
      potential-line-item(label="Max. Fassadenhöhe:" :val="biggestBuildingZone.buildingZone && biggestBuildingZone.buildingZone.frontHeight ? biggestBuildingZone.buildingZone.frontHeight + ' Meter' : null" width="350px")
        template(#tooltipContent)
          analysis-tooltip-content(
            label="Fassadenhöhe"
            tooltip-text="Angabe der maximal möglichen Fassadenhöhe (grün) gemäss dem kommunalen Baugesetz. Die Fassadenhöhe ist die traufseitige Schnittkante der Fassade mit dem Dach."
          )
      potential-line-item(
        label="ÖREB-Auszug zur Parzelle"
        tooltip="Der ÖREB-Auszug gibt Auskunft über die öffentlich-rechtlichen Eigentumsbeschränkungen, welche die abgefragte  Liegenschaft betreffen."
        :val="potential.oerebPdfCdnUrl ? `https://raumpionierestorage.blob.core.windows.net${potential.oerebPdfCdnUrl}` : potential.oerebLink"
        :clickable="true"
        @clicked="event('oreb_link_opened', { eventLabel: 'oreb_linked_clicked', eventCategory: 'B2C' })"
      )
      potential-line-item(
        label="Baureglement Gemeinde"
        tooltip="Das Baureglement der Gemeinde bildet die rechtliche Grundlage und reglementiert insbesondere das Bauen innerhalb der Bauzonen."
        :val="potential.city && potential.city.buildingRegulationsPage ? potential.city.buildingRegulationsPage : ''"
        :clickable="true"
        @clicked="event('building_regulations_link_opened', { eventLabel: 'building_regulations_link_opened', eventCategory: 'B2C' })"
      )
</template>

<style scoped lang="sass">
.potential-details--container
  @media screen and (min-width: 900px)
    display: flex
    flex-direction: row
    justify-content: space-between
    margin-bottom: 40px
  @media screen and (max-width: 900px)
    width: 100%
    margin-bottom: 20px
  .firstBox
    .leadLineItem
      margin-bottom: 10px !important
  .secondBox
    .leadLineItem
      margin-bottom: 12px !important
  .infoBox
    @media screen and (min-width: 900px)
      width: calc(50% - 15px)
    @media screen and (max-width: 900px)
      width: 100%
      margin-bottom: 15px
    .box
      position: relative
      background: rgba(255, 255, 255, 0.7)
      border: 1px solid #D9D9D9
      box-sizing: border-box
      height: 100%
      box-shadow: 0px 15px 30px rgba(166, 166, 166, 0.25)
      border-radius: 10px
      @media screen and (min-width: 900px)
        padding: 20px
        min-height: 274px
      @media screen and (max-width: 900px)
        padding: 15px 20px 15px 20px
      .methodContainer
        display: flex
        justify-content: center
        align-items: center
        margin-left: 5px
      .filledChip
        width: 16px
        height: 16px
        background: #5FEFC0
        border-radius: 50%
      .notFilledChip
        width: 16px
        height: 16px
        background: #F6D156
        border-radius: 50%
      .leadLineItem
        margin-bottom: 7px
      .boxLabel
        font-style: normal
        font-weight: bold
        text-transform: uppercase
        color: #626C85
        margin: 0px 0 12px 0
        @media screen and (min-width: 900px)
          line-height: 130%
          font-size: 16px
        @media screen and (max-width: 900px)
          font-size: 14px
    .potential-line-item--bauZone-container
      width: 100%
      display: flex
      flex-direction: column
      justify-content: center
      @media screen and (min-width: 900px)
        align-items: flex-start
      @media screen and (max-width: 900px) and (min-width: 400px)
        align-items: flex-end
    .itemValue
      font-style: normal
      color: v-bind('global.graphicElementColor')
      position: relative
      @media screen and (min-width: 900px)
        line-height: 130%
        font-size: 16px
        font-weight: 500
      @media screen and (max-width: 900px) and (min-width: 400px)
        font-size: 14px
        font-weight: 600
        display: flex
        flex-direction: row
        justify-content: flex-end
      .svgCircleAndTitleFirstPartContainer
        display: flex
        flex-direction: row
        align-items: center
        margin-right: 4px
        height: 21px
        position: relative
      .itemValueTitle
        height: 21px
        @media screen and (max-width: 900px) and (min-width: 400px)
          text-align: right
      .smallText
        position: absolute
        top: -4px
        right: -7px
        font-size: 10px
      .svgCircle
        background: white
        border-radius: 100%
        padding: 3px
        display: flex
        justify-content: center
        align-items: center
        @media screen and (min-width: 900px)
          margin-right: 6px
          width: 22px
          height: 22px
        @media screen and (max-width: 900px) and (min-width: 400px)
          position: absolute
          top: calc(50% - 9px)
          left: -23px
          width: 16px
          height: 16px
        @media screen and (max-width: 400px)
          margin-right: 6px
          width: 16px
          height: 16px
        .svgCircle--green
          background: #5FEFC0
          border-radius: 100%
          @media screen and (min-width: 900px)
            width: 16px
            height: 16px
          @media screen and (max-width: 900px)
            width: 9px
            height: 9px
        .svgCircle--yellow
          @media screen and (min-width: 900px)
            width: 16px
            height: 16px
          @media screen and (max-width: 900px)
            width: 9px
            height: 9px
          background: #F6D156
          border-radius: 100%
    .linksContainer
      display: grid
      grid-template-columns: 1fr 1fr
      grid-gap: 15px
      margin-top: 5px
      .link
        border: 2px solid #1F47CD
        height: 40px
        border-radius: 10px
        text-decoration: none
        font-weight: 600
        color: #1F47CD
        cursor: pointer
        text-align: center
        vertical-align: middle
        @media screen and (min-width: 1200px)
          font-size: 16px
          line-height: 40px
        @media screen and (max-width: 1999px)
          font-size: 14px
          line-height: 40px
</style>
